import React, { useState, useEffect } from 'react';
import { Button, Input, List, Form, Space, Tag, Typography, message } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { fetchSqlData, insertData, execDDL, deleteData } from '../../../services/api';
import { useLocation } from 'react-router-dom';
import type { CorFormGroup } from '../../../types';

// Utility: get form definition table, data table, and fill title for a given route
function getFormInfoForPath(pathname: string): string[] {
  if (pathname.startsWith('/qaqc')) return ['qaqc_form_definitions', 'qaqc_form_records', 'QAQC Form Groups'];
  if (pathname.startsWith('/it')) return ['it_form_definitions', 'it_form_records', 'IT Form Groups'];
  if (pathname.startsWith('/to')) return ['to_form_definitions', 'to_form_records', 'TO Form Groups'];
  if (pathname.startsWith('/hse')) return ['hse_form_definitions', 'hse_form_records', 'HSE Form Groups'];
  if (pathname.startsWith('/admin')) return ['adm_form_definitions', 'adm_form_records', 'Admin Form Groups'];
  if (pathname.startsWith('/construction')) return ['const_form_definitions', 'const_form_records', 'Construction Form Groups'];
  if (pathname.startsWith('/material')) return ['mat_form_definitions', 'mat_form_records', 'Material Control Form Groups'];
  if (pathname.startsWith('/machinery')) return ['mach_form_definitions', 'mach_form_records', 'Machinery Form Groups'];
  if (pathname.startsWith('/dcc')) return ['dcc_form_definitions', 'dcc_form_records', 'DCC Form Groups'];
  if (pathname.startsWith('/official')) return ['off_form_definitions', 'off_form_records', 'Official Affairs Form Groups'];
  if (pathname.startsWith('/design')) return ['dsgn_form_definitions', 'dsgn_form_records', 'Design & Engineering Form Groups'];
  return ['', '', 'Form Groups'];
}

function pathForRoute(routeName: string): string {
  return '/' + routeName.replace(/^\/+/, '');
}

/** Details view follows the route segment, same rule as FormRecordDetailsList. */
function getDetailsViewForPath(pathname: string): string {
  const segment = pathname.split('/').filter(Boolean)[0] || '';
  return segment ? `vw_${segment}_form_records_details` : '';
}

const DEFAULT_ESSENTIAL_FIELDS = ['form_num', 'form_date'];

// Accept an optional generateViewSQL prop for custom view logic
interface FormGroupCreateProps {
  generateViewSQL?: (module: string, fields: string[]) => string;
}

const FormGroupCreate: React.FC<FormGroupCreateProps> = ({ generateViewSQL }) => {
  const location = useLocation();
  const routeName = location.pathname.split('/')[1] || '';
  const [groups, setGroups] = useState<CorFormGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [fields, setFields] = useState<string[]>([...DEFAULT_ESSENTIAL_FIELDS]);
  const [fieldInput, setFieldInput] = useState('');

  useEffect(() => {
    fetchGroups();
  }, [routeName]);

  const fetchGroups = async () => {
    setLoading(true);
    try {
      const rn = routeName.startsWith('/') ? routeName : '/' + routeName;
      const data: CorFormGroup[] = await fetchSqlData('cor_form_groups', 'route_name', rn);
      setGroups(Array.isArray(data) ? data : []);
    } catch {
      setGroups([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddField = () => {
    let val = fieldInput.trim().toLowerCase().replace(/\s+/g, '_');
    if (val && !fields.includes(val)) {
      setFields([...fields, val]);
    }
    setFieldInput('');
  };

  const handleRemoveField = (name: string) => {
    setFields(fields.filter(f => f !== name));
  };

  const handleSave = async () => {
    if (!groupName.trim()) {
      message.error('Group name is required');
      return;
    }
    if (groups.some((g: CorFormGroup) => g.group_name.toLowerCase() === groupName.trim().toLowerCase())) {
      message.error('Group name must be unique');
      return;
    }
    const payload = {
      route_name: routeName.startsWith('/') ? routeName : '/' + routeName,
      group_name: groupName.trim(),
      essential_field_names: fields
    };
    setLoading(true);
    try {
      // --- REBUILD VIEW: include the new group's fields even before insert ---
      // Aggregate all essential fields from existing groups + the new group
      const allFields = Array.from(new Set([
        ...groups.flatMap(g => g.essential_field_names),
        ...fields
      ]));
      // Use custom or default view SQL generator
      const sql = (generateViewSQL || defaultGenerateViewSQL)(routeName, allFields);
      await execDDL(sql);
      // Now insert the new group
      await insertData('cor_form_groups', payload);
      message.success('Group created');
      setGroupName('');
      setFields([...DEFAULT_ESSENTIAL_FIELDS]);
      await fetchGroups();
      message.success('Form view updated for all groups!');
    } catch (err: any) {
      message.error('Save failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteGroup = async (groupId: number) => {
    setLoading(true);
    try {
      await deleteData('cor_form_groups', 'id', groupId);
      // After deletion, fetch remaining groups
      const remainingGroups: CorFormGroup[] = await fetchSqlData('cor_form_groups', 'route_name', routeName.startsWith('/') ? routeName : '/' + routeName);
      setGroups(Array.isArray(remainingGroups) ? remainingGroups : []);
      // Aggregate all essential fields from remaining groups (robust to null/undefined)
      const allFields = Array.from(new Set(
        (Array.isArray(remainingGroups) ? remainingGroups : [])
          .map(g => Array.isArray(g.essential_field_names) ? g.essential_field_names : [])
          .flat()
          .filter(f => typeof f === 'string' && f.trim() !== '')
      ));
      // Warn if no groups left
      if (remainingGroups.length === 0) {
        message.warning('No groups remain. The view will only include base columns.');
      }
      // Generate SQL and log it
      const sql = (generateViewSQL || defaultGenerateViewSQL)(routeName, allFields);
      // eslint-disable-next-line no-console
      console.log('[DDL] Generated SQL for view:', sql);
      try {
        await execDDL(sql);
        message.success('Group deleted and view updated!');
      } catch (ddlErr: any) {
        // eslint-disable-next-line no-console
        console.error('[DDL ERROR]', ddlErr);
        message.error('View recreation failed: ' + (ddlErr?.message || ddlErr));
      }
    } catch (err: any) {
      message.error('Delete failed: ' + (err?.message || err));
    } finally {
      setLoading(false);
    }
  };

  async function getAllEssentialFields(routeName: string): Promise<string[]> {
    const rn = routeName.startsWith('/') ? routeName : '/' + routeName;
    const data: CorFormGroup[] = await fetchSqlData('cor_form_groups', 'route_name', rn);
    const allFields = new Set<string>();
    data.forEach(g => (g.essential_field_names || []).forEach(f => allFields.add(f)));
    return Array.from(allFields);
  }

  // One row per jsonb key. The details page turns `key` into a column header.
  function defaultGenerateViewSQL(routeName: string, _fields: string[]): string {
    const path = pathForRoute(routeName);
    const [defTable, recordsTable] = getFormInfoForPath(path);
    const viewName = getDetailsViewForPath(path);
    if (!defTable || !recordsTable || !viewName) {
      throw new Error('Unknown form module for this path');
    }
    return `CREATE OR REPLACE VIEW public.${viewName} AS
SELECT
  r.id,
  d.group_id,
  r.form_ref,
  r.fields ->> 'form_num' AS form_num,
  r.fields ->> 'form_date' AS form_date,
  r.status,
  r.form_description,
  r.created_by,
  s.key,
  s.value
FROM public.${recordsTable} r
JOIN public.${defTable} d ON r.form_ref::text = d.form_ref::text
JOIN LATERAL jsonb_each_text(r.fields) s(key, value) ON true;
`;
  }

  function capitalizeFirst(str: string) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  function toUnicodeUpper(str: string) {
    return str.toLocaleUpperCase();
  }

  // Use getFormInfoForPath to get the human-readable title
  const [defTable, recordsTable, formFillTitle] = getFormInfoForPath('/' + routeName.replace(/^\/+/, ''));

  return (
    <div>
      <div style={{ display: 'flex', gap: 32, alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <Typography.Title level={2} style={{ fontWeight: 'bold' }}>{formFillTitle}</Typography.Title>
          <Form layout="vertical" onFinish={handleSave} style={{ marginBottom: 32 }}>
            <Form.Item label="New Group Name" required>
              <Input
                value={groupName}
                onChange={e => setGroupName(e.target.value)}
                onBlur={e => setGroupName(toUnicodeUpper(e.target.value.trim()))}
              />
            </Form.Item>
            <Form.Item label="Essential Field Names">
              <Space wrap>
                {fields.map(f => (
                  DEFAULT_ESSENTIAL_FIELDS.includes(f)
                    ? <Tag key={f}>{f}</Tag>
                    : <Tag closable onClose={() => handleRemoveField(f)} key={f}>{f}</Tag>
                ))}
              </Space>
              <Input
                placeholder="Add field name"
                value={fieldInput}
                onChange={e => setFieldInput(e.target.value)}
                onPressEnter={handleAddField}
                style={{ width: 200, marginTop: 8 }}
                suffix={<PlusOutlined onClick={handleAddField} />}
              />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={loading}>Create Group</Button>
            </Form.Item>
          </Form>
        </div>
        <div style={{ flex: 1, background: '#fafafa', border: '1px solid #eee', borderRadius: 8, padding: 24, minWidth: 320, marginTop: 40 }}>
          <div style={{ fontWeight: 500, fontSize: 16, marginBottom: 12 }}>Existing Groups</div>
          <List
            loading={loading}
            dataSource={groups}
            style={{ marginTop: 0 }}
            bordered
            renderItem={group => {
              const g = group as CorFormGroup;
              return (
                <List.Item
                  actions={[
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={async () => {
                        try {
                          // Check if any form definition references this group
                          const defs = await fetchSqlData(defTable, 'group_id', g.id);
                          if (Array.isArray(defs) && defs.length > 0) {
                            message.error('Cannot delete: Group is in use by existing forms.');
                            return;
                          }
                          await handleDeleteGroup(g.id);
                        } catch (err) {
                          message.error('Failed to delete group');
                        }
                      }}
                    />
                  ]}
                >
                  <Space direction="vertical">
                    <span><b>{g.group_name}</b></span>
                    <span>Fields: {g.essential_field_names.map((f: string) => <Tag key={f}>{f}</Tag>)}</span>
                  </Space>
                </List.Item>
              );
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default FormGroupCreate;
